import { useState, useCallback } from 'react';

const BASE = 'https://fx.softmakon.uz/api';

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  return Math.round(Number(n)).toLocaleString('ru-RU') + " so'm";
};
const fmtKg = (n) => (!n ? '—' : parseFloat(n).toFixed(2) + ' kg');

const TYPE_LABEL = { avia: 'AVIA', fura: 'FURA', avia_tracking: 'AVIA EXPRESS' };
const TYPE_CLS   = {
  avia:         'bg-blue-100 text-blue-700 ring-1 ring-blue-200',
  fura:         'bg-teal-100 text-teal-700 ring-1 ring-teal-200',
  avia_tracking:'bg-violet-100 text-violet-700 ring-1 ring-violet-200',
};

// ─── icons ───────────────────────────────────────────────────────────────────
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
  </svg>
);
const RefreshIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
  </svg>
);
const WarnIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
  </svg>
);
const OkIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
  </svg>
);
const Spinner = () => (
  <div className="w-4 h-4 rounded-full border-2 border-slate-600 border-t-blue-400 animate-spin"/>
);

// ─── anomaly detector ─────────────────────────────────────────────────────────
function detectAnomalies(unpaid, unconfirmed, confirmed) {
  const found = [];
  const orders  = unpaid?.orders  || [];
  const uncList = (unconfirmed?.orders || []).map(o => `${o.serial_number}_${o.type}`);
  const conList = (confirmed?.orders   || []).map(o => `${o.serial_number}_${o.type}`);

  for (const o of orders) {
    const key = `${o.serial_number}_${o.type}`;
    if (uncList.includes(key) && conList.includes(key))
      found.push({ level:'danger', msg:`Partiya ${o.serial_number} (${TYPE_LABEL[o.type]||o.type}) ikki ro'yxatda bir vaqtda mavjud` });
    if ((o.not_paid||0) < 0)
      found.push({ level:'danger', msg:`Partiya ${o.serial_number} qarz manfiy: ${fmt(o.not_paid)} — hisob xatosi` });
    const calc = (o.paid||0) + (o.not_paid||0);
    if (Math.abs(calc - (o.total||0)) > 1)
      found.push({ level:'danger', msg:`Partiya ${o.serial_number}: paid+not_paid (${fmt(calc)}) ≠ total (${fmt(o.total)})` });
    if (o.delivered && (o.not_paid||0) > 0)
      found.push({ level:'warn', msg:`Partiya ${o.serial_number} yetkazilgan, lekin qarz bor: ${fmt(o.not_paid)}` });
  }

  const balance = parseFloat(unpaid?.client_balance||0);
  if (balance < 0)
    found.push({ level:'warn', msg:`Mijoz balansi manfiy: ${fmt(balance)}` });

  for (const o of (unconfirmed?.orders||[]))
    if ((o.amount||0) === 0)
      found.push({ level:'warn', msg:`Yetkazilmagan partiya ${o.serial_number} — summa 0` });

  return found;
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function PaymentChecker() {
  const [clientId,  setClientId]  = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [data,      setData]      = useState(null);   // { unpaid, unconfirmed, confirmed }
  const [filter,    setFilter]    = useState('all');  // all | avia | fura | avia_tracking | anomaly
  const [lastFetch, setLastFetch] = useState('');

  // ── fetch ──
  const fetchAll = useCallback(async (id) => {
    const cid = (id || clientId).trim();
    if (!cid) return;
    setLoading(true);
    setError('');
    setData(null);
    setFilter('all');

    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${BASE}/v1/client-orders/?client_id=${cid}`),
        fetch(`${BASE}/v1/client-unconfirmed-orders/?client_id=${cid}`),
        fetch(`${BASE}/v1/client-confirmed-orders/?client_id=${cid}`),
      ]);

      if (!r1.ok) throw new Error(`API xatolik: ${r1.status}`);
      const [unpaid, unconfirmed, confirmed] = await Promise.all([r1.json(), r2.json(), r3.json()]);

      setData({ unpaid, unconfirmed, confirmed });
      setLastFetch(new Date().toLocaleTimeString('uz-UZ'));
    } catch (e) {
      setError(e.message || 'Server bilan ulanib bo\'lmadi');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const handleKey = (e) => { if (e.key === 'Enter') fetchAll(); };

  // ── derived ──
  const orders     = data?.unpaid?.orders || [];
  const anomalies  = data ? detectAnomalies(data.unpaid, data.unconfirmed, data.confirmed) : [];
  const balance    = parseFloat(data?.unpaid?.client_balance || 0);
  const totalUnpaid= data?.unpaid?.total_unpaid || 0;
  const totalPaid  = data?.unpaid?.total_paid   || 0;

  const unconfMap = {};
  for (const o of (data?.unconfirmed?.orders||[])) unconfMap[`${o.serial_number}_${o.type}`] = o;
  const confMap = {};
  for (const o of (data?.confirmed?.orders||[]))   confMap[`${o.serial_number}_${o.type}`]   = o;

  const filtered = orders.filter(o => {
    if (filter === 'all')    return true;
    if (filter === 'anomaly'){
      const key  = `${o.serial_number}_${o.type}`;
      const dup  = unconfMap[key] && confMap[key];
      const neg  = (o.not_paid||0) < 0;
      const mism = Math.abs((o.paid||0)+(o.not_paid||0)-(o.total||0)) > 1;
      const delU = o.delivered && (o.not_paid||0) > 0;
      return dup || neg || mism || delU;
    }
    return o.type === filter;
  });

  const hasData = !!data;

  // ── row anomaly flags ──
  const rowFlags = (o) => {
    const flags = [];
    const key = `${o.serial_number}_${o.type}`;
    if (unconfMap[key] && confMap[key])               flags.push('IKKI RO\'YXAT');
    if ((o.not_paid||0) < 0)                          flags.push('NEG QARZ');
    if (Math.abs((o.paid||0)+(o.not_paid||0)-(o.total||0)) > 1) flags.push('SUM XATO');
    if (o.delivered && (o.not_paid||0) > 0)           flags.push('YETKAZILGAN+QARZ');
    return flags;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0d0f13] text-slate-200 font-sans">

      {/* ══ HEADER ══ */}
      <header className="sticky top-0 z-30 bg-[#111318] border-b border-[#1e2333] px-6 py-4 flex items-center gap-3">
        <span className="font-mono text-xs font-semibold text-emerald-400 tracking-widest uppercase">
          FX_AUDIT
        </span>
        <span className="text-[#1e2333]">/</span>
        <span className="text-sm font-medium text-slate-300">To'lov monitoring</span>
        {lastFetch && (
          <span className="ml-auto font-mono text-[10px] text-slate-600">
            yangilandi: {lastFetch}
          </span>
        )}
      </header>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* ══ SEARCH ══ */}
        <div className="bg-[#111318] border border-[#1e2333] rounded-xl p-4 flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1.5 flex-1 min-w-[200px]">
            <label className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">
              Mijoz ID kodi
            </label>
            <input
              value={clientId}
              onChange={e => setClientId(e.target.value.toUpperCase())}
              onKeyDown={handleKey}
              placeholder="010001"
              maxLength={7}
              className="bg-[#0d0f13] border border-[#1e2333] text-slate-200 font-mono text-sm
                         rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500
                         focus:ring-1 focus:ring-blue-500/40 placeholder-slate-600 w-full max-w-xs"
            />
          </div>
          <button
            onClick={() => fetchAll()}
            disabled={loading || !clientId.trim()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500
                       disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm
                       font-semibold transition-colors"
          >
            {loading ? <Spinner /> : <SearchIcon />}
            Tekshirish
          </button>
          {hasData && (
            <button
              onClick={() => fetchAll()}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#1e2333]
                         hover:border-blue-500/50 hover:text-blue-400 text-slate-400 text-sm
                         transition-colors disabled:opacity-40"
            >
              <RefreshIcon /> Yangilash
            </button>
          )}
        </div>

        {/* ══ ERROR ══ */}
        {error && (
          <div className="flex items-center gap-3 bg-red-950/40 border border-red-800/40
                          text-red-400 rounded-xl px-5 py-3.5 text-sm">
            <WarnIcon /> {error}
          </div>
        )}

        {/* ══ LOADING skeleton ══ */}
        {loading && !data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[...Array(4)].map((_,i) => (
              <div key={i} className="bg-[#111318] border border-[#1e2333] rounded-xl p-4 animate-pulse">
                <div className="h-2 bg-[#1e2333] rounded w-20 mb-3"/>
                <div className="h-6 bg-[#1e2333] rounded w-28"/>
              </div>
            ))}
          </div>
        )}

        {hasData && (
          <>
            {/* ══ SUMMARY CARDS ══ */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { label: 'Joriy balans',   value: fmt(balance),     color: balance >= 0 ? 'text-emerald-400' : 'text-red-400' },
                { label: "Jami qarz",      value: fmt(totalUnpaid), color: totalUnpaid > 0 ? 'text-red-400' : 'text-emerald-400' },
                { label: "To'langan",      value: fmt(totalPaid),   color: 'text-emerald-400' },
                { label: 'Partiyalar',     value: orders.length,    color: 'text-slate-200' },
                { label: 'Anomaliyalar',   value: anomalies.length, color: anomalies.length > 0 ? 'text-amber-400' : 'text-emerald-400' },
              ].map(c => (
                <div key={c.label} className="bg-[#111318] border border-[#1e2333] rounded-xl px-4 py-4">
                  <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mb-2">{c.label}</p>
                  <p className={`font-mono text-lg font-semibold ${c.color}`}>{c.value}</p>
                </div>
              ))}
            </div>

            {/* ══ ALERTS ══ */}
            <div className="space-y-2">
              {anomalies.length === 0 ? (
                <div className="flex items-center gap-2.5 bg-emerald-950/30 border border-emerald-800/30
                                text-emerald-400 rounded-xl px-5 py-3 text-sm">
                  <OkIcon /> Barcha tekshiruvlar muvaffaqiyatli — anomaliya topilmadi
                </div>
              ) : (
                anomalies.map((a, i) => (
                  <div key={i} className={`flex items-start gap-2.5 rounded-xl px-5 py-3 text-sm ${
                    a.level === 'danger'
                      ? 'bg-red-950/30 border border-red-800/30 text-red-400'
                      : 'bg-amber-950/30 border border-amber-800/30 text-amber-400'
                  }`}>
                    <WarnIcon /> {a.msg}
                  </div>
                ))
              )}
            </div>

            {/* ══ BALANCE DETAIL ══ */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Balans */}
              <div className="bg-[#111318] border border-[#1e2333] rounded-xl p-4">
                <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mb-3">
                  Balans tahlili
                </p>
                {[
                  ['Joriy balans',     fmt(balance),             balance >= 0 ? 'text-emerald-400':'text-red-400'],
                  ['Jami qarz',        fmt(totalUnpaid),         totalUnpaid > 0 ? 'text-red-400':'text-emerald-400'],
                  ["To'langan",        fmt(totalPaid),           'text-emerald-400'],
                  ['Balans − qarz',    fmt(balance - totalUnpaid), (balance-totalUnpaid) >= 0 ? 'text-emerald-400':'text-red-400'],
                ].map(([k,v,c]) => (
                  <div key={k} className="flex justify-between items-center py-2 border-b border-[#1e2333] last:border-0">
                    <span className="text-slate-500 text-xs">{k}</span>
                    <span className={`font-mono text-xs font-semibold ${c}`}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Yetkazilmagan */}
              <div className="bg-[#111318] border border-[#1e2333] rounded-xl p-4">
                <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mb-3">
                  Yetkazib berish (kutmoqda)
                </p>
                {(() => {
                  const unc = data.unconfirmed?.orders || [];
                  const totalAmt = unc.reduce((s,o)=>s+(o.amount||0), 0);
                  const totalWt  = unc.reduce((s,o)=>s+(o.weight||0), 0);
                  return [
                    ["To'langan, yetkazilmagan", unc.length + ' ta', 'text-amber-400'],
                    ['Umumiy summa',              fmt(totalAmt),      totalAmt>0?'text-amber-400':'text-emerald-400'],
                    ["Umumiy og'irlik",           fmtKg(totalWt),     'text-slate-300'],
                  ].map(([k,v,c]) => (
                    <div key={k} className="flex justify-between items-center py-2 border-b border-[#1e2333] last:border-0">
                      <span className="text-slate-500 text-xs">{k}</span>
                      <span className={`font-mono text-xs font-semibold ${c}`}>{v}</span>
                    </div>
                  ));
                })()}
              </div>

              {/* Yetkazilgan */}
              <div className="bg-[#111318] border border-[#1e2333] rounded-xl p-4">
                <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mb-3">
                  Yetkazib berilgan
                </p>
                {(() => {
                  const conf = data.confirmed?.orders || [];
                  const totalWt = conf.reduce((s,o)=>s+(o.weight||0), 0);
                  return [
                    ['Yetkazilgan partiyalar', conf.length + ' ta',    'text-emerald-400'],
                    ["Umumiy og'irlik",         fmtKg(totalWt),        'text-slate-300'],
                    ['Grand total (API)',        fmt(data.unpaid?.grand_total), 'text-slate-300'],
                  ].map(([k,v,c]) => (
                    <div key={k} className="flex justify-between items-center py-2 border-b border-[#1e2333] last:border-0">
                      <span className="text-slate-500 text-xs">{k}</span>
                      <span className={`font-mono text-xs font-semibold ${c}`}>{v}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* ══ FILTER TABS ══ */}
            <div className="flex gap-2 flex-wrap">
              {[
                { key:'all',          label:'Barchasi',     count: orders.length },
                { key:'avia',         label:'AVIA',         count: orders.filter(o=>o.type==='avia').length },
                { key:'fura',         label:'FURA',         count: orders.filter(o=>o.type==='fura').length },
                { key:'avia_tracking',label:'AVIA EXPRESS', count: orders.filter(o=>o.type==='avia_tracking').length },
                { key:'anomaly',      label:'⚠ Anomaliyalar', count: orders.filter(o=>rowFlags(o).length>0).length },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold
                              border transition-colors ${
                    filter === tab.key
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-[#111318] border-[#1e2333] text-slate-400 hover:border-blue-500/50 hover:text-blue-400'
                  }`}
                >
                  {tab.label}
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                    filter === tab.key ? 'bg-blue-500 text-white' : 'bg-[#1e2333] text-slate-500'
                  }`}>{tab.count}</span>
                </button>
              ))}
            </div>

            {/* ══ TABLE ══ */}
            <div className="bg-[#111318] border border-[#1e2333] rounded-xl overflow-hidden">
              {filtered.length === 0 ? (
                <div className="py-16 text-center text-slate-600 text-sm">
                  Bu filtr uchun partiya topilmadi
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[780px]">
                    <thead>
                      <tr className="border-b border-[#1e2333]">
                        {['Partiya №','Turi',"To'langan",'Qarz','Jami','Holat',"Og'irlik",'Xulosa'].map(h => (
                          <th key={h} className="px-4 py-3 text-left font-mono text-[10px] text-slate-500
                                                 uppercase tracking-widest whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((o, i) => {
                        const flags   = rowFlags(o);
                        const isAnom  = flags.length > 0;
                        const inUnc   = unconfMap[`${o.serial_number}_${o.type}`];
                        const inConf  = confMap[`${o.serial_number}_${o.type}`];

                        let statusEl;
                        if ((o.not_paid||0) > 0)
                          statusEl = <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0 shadow-[0_0_4px] shadow-red-500"/>To'lanmagan</span>;
                        else if (o.delivered)
                          statusEl = <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 shadow-[0_0_4px] shadow-emerald-500"/>Yetkazildi</span>;
                        else
                          statusEl = <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0 shadow-[0_0_4px] shadow-amber-500"/>Kutmoqda</span>;

                        return (
                          <tr key={i}
                            className={`border-b border-[#1e2333]/50 transition-colors
                                        ${isAnom ? 'bg-red-950/10' : 'hover:bg-[#151920]'}`}>
                            <td className="px-4 py-3 font-mono text-sm font-semibold text-slate-200">
                              {o.serial_number}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold ${TYPE_CLS[o.type]||'bg-slate-800 text-slate-400'}`}>
                                {TYPE_LABEL[o.type]||o.type}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs font-semibold text-emerald-400">
                              {fmt(o.paid)}
                            </td>
                            <td className={`px-4 py-3 font-mono text-xs font-semibold ${(o.not_paid||0)>0?'text-red-400':'text-emerald-400'}`}>
                              {fmt(o.not_paid)}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-500">
                              {fmt(o.total)}
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
                              {statusEl}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-500">
                              {fmtKg(o.weight)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {inUnc && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-teal-900/50 text-teal-400 ring-1 ring-teal-700/50">
                                    YETKAZILMAGAN
                                  </span>
                                )}
                                {inConf && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-900/50 text-blue-400 ring-1 ring-blue-700/50">
                                    YETKAZILGAN
                                  </span>
                                )}
                                {flags.map(f => (
                                  <span key={f} className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-900/40 text-red-400 ring-1 ring-red-700/40">
                                    {f}
                                  </span>
                                ))}
                                {!inUnc && !inConf && flags.length===0 && (
                                  <span className="text-[10px] text-slate-600 font-mono">—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ══ EMPTY STATE ══ */}
        {!hasData && !loading && !error && (
          <div className="py-24 text-center space-y-3">
            <div className="font-mono text-4xl text-slate-700">◎</div>
            <p className="text-slate-500 text-sm">Mijoz ID kodini kiriting va tekshirish tugmasini bosing</p>
            <p className="font-mono text-[11px] text-slate-700">
              {BASE}/v1/client-orders/ · client-unconfirmed-orders · client-confirmed-orders
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
